// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export const LOGIN_ERROR_MESSAGE = 'Invalid username or password. Please check your credentials and try again.';

const CLOCK_SKEW_MS = 60 * 1000;

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password, source }, { rejectWithValue }) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_IAM_BASE_URL}${process.env.REACT_APP_LOGIN_ENDPOINT}`,
        { username, password }
      );

      const token = resp.data?.auth_token;
      if (typeof token !== 'string' || !token.trim()) {
        throw new Error('Invalid token response');
      }

      const decoded = jwtDecode(token);
      const { parentClientId, merchantId, exp } = decoded ?? {};

      if (!parentClientId || !merchantId) {
        throw new Error('Invalid token payload');
      }
      const expMs = typeof exp === 'number' ? exp * 1000 : 0;
      if (expMs < Date.now() - CLOCK_SKEW_MS) {
        throw new Error('Token already expired');
      }

      try {
        localStorage.setItem('authToken', token);
        localStorage.setItem('parentClientId', parentClientId);
        localStorage.setItem('merchantId', merchantId);
      } catch (storageErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Auth storage write failed:', storageErr);
        }
        throw new Error('Session storage unavailable');
      }

      const sourceValue = source || localStorage.getItem('source') || 'wayuvega';
      if (sourceValue) {
        try {
          localStorage.setItem('source', sourceValue);
        } catch (_) {}
      }

      return { token, parentClientId, merchantId, exp, source: sourceValue };
    } catch (err) {
      if (process.env.NODE_ENV !== 'production' && err?.message) {
        console.warn('[Auth]', err.message);
      }
      return rejectWithValue(LOGIN_ERROR_MESSAGE);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  token: null,
  parentClientId: null,
  merchantId: null,
  userExp: null,     // Unix timestamp (in seconds) when token expires
  loading: false,
  error: null,
  initialized: false,
  // track which branded login was used. initialize from localStorage if present.
  source: localStorage.getItem('source') || 'wayuvega',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.parentClientId = null;
      state.merchantId = null;
      state.userExp = null;
      state.error = null;
      state.initialized = true;
      // Clear localStorage auth keys but preserve source
      localStorage.removeItem('authToken');
      localStorage.removeItem('parentClientId');
      localStorage.removeItem('merchantId');
      // DO NOT remove localStorage 'source' here — keep it so logout redirects to the right login
    },
    restoreSession(state, action) {
      const { token, user } = action.payload;
      
      // Extract fields from decoded token
      const parentClientId = user.parentClientId || user.clientId;
      const merchantId = user.merchantId;
      const exp = user.exp;
      const source = user.source || localStorage.getItem('source') || 'wayuvega';
      
      // Update Redux state
      state.isAuthenticated = true;
      state.token = token;
      state.parentClientId = parentClientId;
      state.merchantId = merchantId;
      state.userExp = exp;
      state.error = null;
      state.initialized = true;
      state.source = source;
      
      // 🔥 PERSIST TO LOCALSTORAGE
      try {
        localStorage.setItem('authToken', token);
        if (parentClientId) localStorage.setItem('parentClientId', parentClientId);
        if (merchantId) localStorage.setItem('merchantId', merchantId);
        localStorage.setItem('source', source);
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    },
    markInitialized(state) { state.initialized = true; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, parentClientId, merchantId, exp, source } = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = token;
        state.parentClientId = parentClientId;
        state.merchantId = merchantId;
        state.userExp = exp;
        state.error = null;
        if (source) {
          state.source = source;
          try { localStorage.setItem('source', source); } catch (e) {}
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        if (!state.isAuthenticated) {
          state.error = action.payload || LOGIN_ERROR_MESSAGE;
        }
      });
  },
});

export const { logout, restoreSession, markInitialized } = authSlice.actions;
export default authSlice.reducer;
